/**
 * American Vek: quantity buying on the product page.
 * - <av-volume-pricing>: tier rows set the product form quantity; the row matching the current
 *   quantity is highlighted.
 * - <av-size-grid>: one quantity per size, running total and next-tier hint, and one button that
 *   adds every size through Cart AJAX. Fires the same CartLinesUpdateEvent as the native product
 *   form so the cart drawer, cart items and cart count update.
 * Tiers ("6:5,12:8" = from 6 units 5% off, from 12 units 8% off) are display only; the discount is a
 * Shopify automatic discount with the same tiers.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** @param {string} raw */
function parseTiers(raw) {
  return (raw || '')
    .split(',')
    .map((pair) => pair.split(':').map(Number))
    .filter(([min, pct]) => min > 0 && pct > 0)
    .map(([min, pct]) => ({ min, pct }))
    .sort((a, b) => a.min - b.min);
}

/** @param {{min:number,pct:number}[]} tiers @param {number} units */
function tierFor(tiers, units) {
  return tiers.filter((tier) => units >= tier.min).pop() || null;
}

class AvVolumePricing extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.addEventListener('click', this.onRowClick);
    this.section.addEventListener('input', this.onQuantityChange);
    this.section.addEventListener('change', this.onQuantityChange);
    this.section.addEventListener('click', this.onStepperClick);
    this.highlight();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRowClick);
    this.section?.removeEventListener('input', this.onQuantityChange);
    this.section?.removeEventListener('change', this.onQuantityChange);
    this.section?.removeEventListener('click', this.onStepperClick);
  }

  get quantityInput() {
    return /** @type {HTMLInputElement|null} */ (
      this.section.querySelector('product-form-component input[name="quantity"]')
    );
  }

  /** @param {MouseEvent} event */
  onRowClick = (event) => {
    const row = /** @type {HTMLElement|null} */ (/** @type {Element} */ (event.target).closest('.av-volume__row'));
    const input = this.quantityInput;
    if (!row || !input) return;
    input.value = row.dataset.min || '1';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.highlight();
  };

  /** @param {Event} event */
  onQuantityChange = (event) => {
    if (event.target === this.quantityInput) this.highlight();
  };

  /** Quantity +/- buttons change the value without an input event; re-read after they run. */
  onStepperClick = () => requestAnimationFrame(() => this.highlight());

  highlight() {
    const units = Number(this.quantityInput?.value) || 1;
    this.querySelectorAll('.av-volume__row').forEach((row) => {
      const el = /** @type {HTMLElement} */ (row);
      const min = Number(el.dataset.min);
      const max = el.dataset.max ? Number(el.dataset.max) : Infinity;
      el.setAttribute('aria-current', String(units >= min && units <= max));
    });
  }
}

class AvSizeGrid extends HTMLElement {
  connectedCallback() {
    this.tiers = parseTiers(this.dataset.tiers || '');
    this.money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: this.dataset.currency || 'BRL' });
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.update);
    this.addEventListener('change', this.onChange);
    this.update();
  }

  /** @param {string} name */
  ref(name) {
    return /** @type {HTMLElement} */ (this.querySelector(`[data-ref="${name}"]`));
  }

  get inputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('.av-size-grid__input:not(:disabled)')]);
  }

  /** @param {HTMLInputElement} input @param {number} value */
  clamp(input, value) {
    const max = input.max ? Number(input.max) : Infinity;
    return Math.max(0, Math.min(Number.isFinite(value) ? Math.floor(value) : 0, max));
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const step = /** @type {HTMLElement|null} */ (target.closest('.av-size-grid__step'));
    if (step) {
      const input = /** @type {HTMLInputElement} */ (step.parentElement?.querySelector('input'));
      input.value = String(this.clamp(input, Number(input.value) + Number(step.dataset.step)));
      this.update();
      return;
    }
    if (target.closest('[data-ref="add"]')) this.add();
  };

  /** @param {Event} event */
  onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.classList?.contains('av-size-grid__input')) return;
    input.value = String(this.clamp(input, Number(input.value)));
    this.update();
  };

  update = () => {
    let units = 0;
    let amount = 0;
    for (const input of this.inputs) {
      const qty = this.clamp(input, Number(input.value));
      units += qty;
      amount += qty * Number(input.dataset.price);
    }
    const tier = tierFor(this.tiers, units);
    const next = this.tiers.find((t) => t.min > units);
    const total = tier ? (amount * (100 - tier.pct)) / 100 : amount;

    this.ref('units').textContent = `${units} ${units === 1 ? 'unidade' : 'unidades'}`;
    this.ref('amount').textContent = units ? this.money.format(total / 100) : '';

    let hint = '';
    if (units && tier && next) {
      hint = `${tier.pct}% de desconto aplicado. Faltam ${next.min - units} para ${next.pct}%.`;
    } else if (units && tier) {
      hint = `${tier.pct}% de desconto aplicado no carrinho.`;
    } else if (next) {
      const missing = next.min - units;
      hint = `${units ? 'Faltam' : 'A partir de'} ${units ? missing : next.min} ${
        (units ? missing : next.min) === 1 ? 'unidade' : 'unidades'
      } para ${next.pct}% de desconto.`;
    }
    this.ref('hint').textContent = hint;

    const add = /** @type {HTMLButtonElement} */ (this.ref('add'));
    add.disabled = units === 0;
    this.units = units;
  };

  async add() {
    const items = this.inputs
      .map((input) => ({ id: Number(input.dataset.variantId), quantity: this.clamp(input, Number(input.value)) }))
      .filter((item) => item.quantity > 0);
    if (!items.length) return;

    const add = /** @type {HTMLButtonElement} */ (this.ref('add'));
    const error = this.ref('error');
    error.hidden = true;
    add.disabled = true;
    add.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const routes = window.Theme?.routes || {};
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erro ao adicionar');

      const cart = await fetch(`${routes.cart_url || '/cart'}.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'av-size-grid',
          sourceId: this.dataset.productId,
          itemCount: this.units,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      this.inputs.forEach((input) => (input.value = '0'));
      this.update();
    } catch (err) {
      deferred.reject(err);
      error.textContent = err instanceof Error ? err.message : 'Não foi possível adicionar. Tente novamente.';
      error.hidden = false;
      add.disabled = false;
    } finally {
      add.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('av-volume-pricing')) customElements.define('av-volume-pricing', AvVolumePricing);
if (!customElements.get('av-size-grid')) customElements.define('av-size-grid', AvSizeGrid);
